import React, { useEffect, useState } from "react";
import "./MobileOtpVerification.css";
import { Strings } from "../../../../utils/strings";
import { WebChatApi } from "../../../../apis/webChat.api";
import LoadingIndicator from "../../../loadingIndicator/LoadingIndicator";

type props = {
  email: string;
  setAuthToken: React.Dispatch<React.SetStateAction<string | undefined>>;
};

export const MobileOTPVerification: React.FC<props> = ({
  email,
  setAuthToken,
}) => {
  const [otp, setOTP] = useState("");
  const [seconds, setSeconds] = useState(30);
  const [isOtpBtnEnabled, setOtpBtnEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const resetErrorAndSuccessMessage = () => {
    if (error) {
      setError("");
    }
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    resetErrorAndSuccessMessage();

    if (e.target.value.length <= 4) {
      setOTP(e.target.value);
    }
  };

  useEffect(() => {
    if (otp.length === 4) {
      setOtpBtnEnabled(true);
    } else {
      setOtpBtnEnabled(false);
    }
  }, [otp]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (seconds > 0) {
        setSeconds(seconds - 1);
      }
      if (seconds === 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [seconds]);

  const handleResendOTP = async () => {
    resetErrorAndSuccessMessage();

    try {
      setLoading(true);
      await WebChatApi.otpCodeSend({
        email: email ?? "",
      });
      setLoading(false);
      setSeconds(30);
    } catch (error: any) {
      setLoading(false);
      setError(error.message);
    }
  };

  const handleVerifyOtp = async (enteredOtp: string) => {
    if (enteredOtp?.length === 4) {
      try {
        setLoading(true);
        const response = await WebChatApi.otpCodeVerify({
          email: email ?? "",
          otp: enteredOtp,
        });
        if (response.jwt) {
          setAuthToken(response.jwt);
        }
        setLoading(false);
      } catch (error: any) {
        setLoading(false);
        setError(error.message);
      }
    } else {
      setError(Strings.ENTER_VALID_OTP);
    }
  };

  return (
    <div className="mobile-otp-container">
      <div className="mobile-otp-details-container">
        <span className="mobile-otp-title">{Strings.OTP_VERIFICATION}</span>
        <span className="mobile-otp-code-sent">{Strings.CODE_SENT_TO}</span>
        <span className="mobile-otp-code-email">{email}</span>
      </div>
      <div className="mobile-otp-code-text-input-container">
        <input
          type="text"
          value={otp}
          placeholder={Strings.ENTER_CODE}
          onChange={handleOtpChange}
          className={
            otp
              ? "mobile-otp-code-text-input-value"
              : "mobile-otp-code-text-input"
          }
        />
        {error && email && (
          <p style={{ color: "red", fontSize: 13, textAlign: "center" }}>
            {error}
          </p>
        )}
        <button
          disabled={!isOtpBtnEnabled}
          style={!isOtpBtnEnabled ? { opacity: 0.5 } : {}}
          type="button"
          className="mobile-otp-code-verify-btn"
          onClick={() => {
            handleVerifyOtp(otp);
          }}>
          Verify
        </button>
      </div>
      <div className="mobile-otp-resent-code-container">
        <span className="mobile-otp-resend-code">
          {Strings.RESEND_CODE}
          <span className="mobile-otp-resend-code-timer">
            {seconds > 0 ? `00:${String(seconds).padStart(2, "0")}s` : "00:00"}
          </span>
        </span>
        {seconds === 0 && (
          <span
            onClick={() => {
              handleResendOTP();
            }}
            className="mobile-otp-resend-text">
            {Strings.RESEND_AGAIN}
          </span>
        )}
      </div>
      <LoadingIndicator className="mobile-otp-overlay" loading={loading} />
    </div>
  );
};
